import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as ReactMapGL from "react-map-gl/mapbox";
import { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import { bbox } from "@turf/bbox";
import { featureCollection, point } from "@turf/helpers";
import { LngLatBounds, EasingOptions } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FeatureCollection, Geometry, Point } from "geojson";
import { MappingContext } from "@app/mapping/MappingContext";
import { RecenterControl } from "@app/shared/mapping/RecenterControl";
import { MapboxSupportAlert } from "@app/shared/mapping/MapboxSupportAlert";
import { MapPropertyKeysEnum } from "@app/projects/network-assessment/maps/mapPropertyKeysEnum";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { MemberMapResults, ProviderMapResults } from "@app/projects/results/common/mapping";
import { UserContext } from "@app/user/UserContext";
import { canViewTerminatedProviders } from "@app/projects/terminationRoleGate";
import { CountyModel, LegendItem, ZipCodeModel } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { ZipCoverageMapLayerControl } from "@app/projects/zip-projects/maps/ZipCoverageMapLayerControl";
import ZipCoverageMapLegendControl from "@app/projects/zip-projects/maps/ZipCoverageMapLegendControl";
import { QesIcons } from "@app/shared/icons/qesIcons";
import {
  COUNTY_BOUNDARY_COLOR,
  COUNTY_BOUNDARY_LABEL_LAYER_ID,
  COUNTY_BOUNDARY_LAYER_ID,
  COUNTY_BOUNDARY_SOURCE_ID,
  COUNTY_MIN_ZOOM_LEVEL,
  EXCLUDED_PROVIDER_COLOR,
  LEFT_PANEL_VIEW_PADDING,
  MARKET_PROVIDER_COLOR,
  MAX_FIT_ZOOM_LEVEL,
  MEMBER_LAYER_ID,
  MEMBER_SOURCE_ID,
  MEMBERS_IMAGE_ID,
  MEMBERS_WITH_ACCESS_COLOR,
  MEMBERS_WITHOUT_ACCESS_COLOR,
  NETWORK_PROVIDER_COLOR,
  PROSPECT_PROVIDER_COLOR,
  PROVIDER_HIGHLIGHTED_LAYER_ID,
  PROVIDER_ICON_IMAGE_ID,
  PROVIDER_ICON_SELECTED_SIZE,
  PROVIDER_ICON_SHADOW_COLOR,
  PROVIDER_ICON_UNSELECTED_MAX_SIZE,
  PROVIDER_ICON_UNSELECTED_MIN_SIZE,
  PROVIDERS_LAYER_ID,
  PROVIDERS_SOURCE_ID,
  SELECTED_PROVIDER_SOURCE_ID,
  TARGET_PROVIDER_COLOR,
  TERMINATED_PROVIDER_COLOR,
  VIEW_PADDING,
  ZIP_BOUNDARY_COLOR,
  ZIP_BOUNDARY_LABEL_LAYER_ID,
  ZIP_BOUNDARY_MIN_ZOOM_LEVEL,
  ZIP_FOCUSED_BOUNDARY_LAYER_ID,
  ZIP_FOCUSED_BOUNDARY_SOURCE_ID,
  ZIP_LABEL_SOURCE_ID,
} from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";
import { EXCLUDE_ZIP_POINTS_FILTER, INCLUDE_ZIP_POINTS_FILTER } from "@app/shared/mapping/zipPoints";

interface ZipCoverageMapProps {
  boundaries: FeatureCollection<Geometry, ZipCodeModel> | null;
  /** The single focused zip's own boundary — drives the outline layer and map framing.
   *  Distinct from `boundaries` (the whole county), which only powers zip labels. */
  focusedZipBoundary: FeatureCollection<Geometry, ZipCodeModel> | null;
  countyBoundaries: FeatureCollection<Geometry, CountyModel> | null;
  memberFeatures: FeatureCollection<Point, MemberMapResults>;
  /** Overlays the map's left edge; the map spans the full width underneath it, so collapsing the
   *  panel never resizes the Mapbox canvas. */
  leftPanel?: ReactNode;
  /** Controls overlaid on the map itself (e.g. the floating Update Inputs / Filters
   *  buttons shown after Create). */
  mapOverlay?: ReactNode;
  mapStyle: string;
  onMapStyleChange: (style: string) => void;
  showLayerControl: boolean;
  showMembersWithAccess: boolean;
  onShowMembersWithAccessChange: (checked: boolean) => void;
  showMembersWithoutAccess: boolean;
  onShowMembersWithoutAccessChange: (checked: boolean) => void;
  showZipBoundary: boolean;
  onShowZipBoundaryChange: (checked: boolean) => void;
  showZipPoints: boolean;
  /** Labels Zip Points regardless of `showZipPoints` — the multi-zip label source holds only selected zips. */
  alwaysLabelZipPoints?: boolean;
  onShowZipPointsChange: (checked: boolean) => void;
  showCounty: boolean;
  onShowCountyChange: (checked: boolean) => void;
  providerFeatures: FeatureCollection<Point, ProviderMapResults>;
  framingProviderFeatures?: FeatureCollection<Point, ProviderMapResults>;
  selectedProvider: ProviderMapResults | null;
  onProviderSelect: (provider: ProviderMapResults) => void;
  providerPopup?: ReactNode;
  /** Whether the applied run configuration is the latest published. Gates the action-status pin colors. */
  isLatestPublishedRunConfiguration: boolean;
  /** Which provider sources are on the map, for the legend's Providers section. */
  selectedProviderSources: ProviderNetworkTypeEnum[];
  hasTargetedProviders: boolean;
  hasTerminatedProviders: boolean;
  hasExcludedProviders: boolean;
  onMapBoundsChange: (bounds: LngLatBounds | null) => void;
}

function loadImagesToMap(map: ReactMapGL.MapInstance): void {
  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const images = [{ id: MEMBERS_IMAGE_ID, path: `${imagesDir}Members.png` }];
  images.forEach(({ id, path }) => {
    if (!map.hasImage(id)) {
      map.loadImage(path, (error: Error | null, image: HTMLImageElement | undefined) => {
        if (error || !image) return;

        if (!map.hasImage(id)) {
          map.addImage(id, image, { sdf: true });
        }
      });
    }
  });
}

/**
 * Presentational ZIP coverage map: left panel column (`leftPanel`) beside a Mapbox map
 * (ZIP boundary + provider layers) + map-overlay controls (`mapOverlay`). `preserveDrawingBuffer` on for
 * future export; add member/adequacy layers here as more <Source>/<Layer>.
 *
 * SOURCE (copied-thin, 199592): network-coverage-map/NetworkCoverageMap.tsx — Map shell +
 * boundary <Source>/<Layer> + fit-to-bounds. ZIP-owned (county untouched).
 * CONSOLIDATION (199592): extract shared `shared/mapping/coverage/` (CoverageMapShell +
 * BoundaryLayer) — stateless/prop-driven; no shared project context.
 */
export const ZipCoverageMap: FC<ZipCoverageMapProps> = ({
  boundaries,
  focusedZipBoundary,
  countyBoundaries,
  memberFeatures,
  leftPanel,
  mapOverlay,
  mapStyle,
  onMapStyleChange,
  showLayerControl,
  showMembersWithAccess,
  onShowMembersWithAccessChange,
  showMembersWithoutAccess,
  onShowMembersWithoutAccessChange,
  showZipBoundary,
  onShowZipBoundaryChange,
  showZipPoints,
  alwaysLabelZipPoints = false,
  onShowZipPointsChange,
  showCounty,
  onShowCountyChange,
  providerFeatures,
  framingProviderFeatures,
  selectedProvider,
  onProviderSelect,
  providerPopup,
  isLatestPublishedRunConfiguration,
  selectedProviderSources,
  hasTargetedProviders,
  hasTerminatedProviders,
  hasExcludedProviders,
  onMapBoundsChange,
}) => {
  const mappingContext = useContext(MappingContext);
  const userContext = useContext(UserContext);
  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ReactMapGL.ViewState>(mappingContext.defaultViewState);
  const [mapLoaded, setMapLoaded] = useState(false);

  const imagesDir = `${window.customUIBaseUrl}/images/`;

  const easingOptions = (): EasingOptions => ({
    duration: 1000,
    // A zero-area extent would otherwise make Mapbox zoom all the way in.
    maxZoom: MAX_FIT_ZOOM_LEVEL,
    // Keeps the fitted boundaries clear of the left panel overlaying the map.
    padding: { bottom: VIEW_PADDING, left: LEFT_PANEL_VIEW_PADDING, right: VIEW_PADDING, top: VIEW_PADDING },
    retainPadding: false,
  });

  // Frame on the focused zip AND its providers, so providers servicing it from outside the boundary stay on
  // screen (202310). With every provider inside, the union equals the boundary and framing is unchanged.
  const framingProviders = framingProviderFeatures ?? providerFeatures;
  const framingFeatures = useMemo<GeoJSON.Feature[]>(
    () => [
      ...((focusedZipBoundary?.features ?? []) as GeoJSON.Feature[]),
      ...(framingProviders.features as GeoJSON.Feature[]),
    ],
    [focusedZipBoundary, framingProviders],
  );

  /**
   * Fits the map to `framingFeatures`; false when there is nothing usable, so the caller can fall back.
   *
   * The finite check is load-bearing: the zip-border endpoint returns 200 with features whose geometry can be
   * null, which passes a length check but makes bbox return Infinity — that is what zoomed some zips to
   * maximum zoom with no boundary drawn.
   */
  const fitToData = useCallback((): boolean => {
    const map = mapRef.current;
    if (!map || framingFeatures.length === 0) return false;

    const [minLng, minLat, maxLng, maxLat] = bbox({ features: framingFeatures, type: "FeatureCollection" });
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return false;

    map.fitBounds(new LngLatBounds([minLng, minLat], [maxLng, maxLat]), easingOptions());
    return true;
  }, [framingFeatures]);

  // Gating on mapLoaded avoids a no-op fitBounds before the Mapbox instance exists. With nothing to fit —
  // e.g. after "Update Inputs" — zoom back out to the default view, matching county.
  useEffect(() => {
    if (!mapLoaded) {
      return;
    }
    if (!fitToData()) {
      setViewState(mappingContext.defaultViewState);
    }
  }, [mapLoaded, fitToData, mappingContext.defaultViewState]);

  const onMapLoad = useCallback((): void => {
    setMapLoaded(true);
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.on("styledata", () => loadImagesToMap(map));
    loadImagesToMap(map);
  }, []);

  const recenter = useCallback((): void => {
    if (!fitToData()) {
      setViewState(mappingContext.defaultViewState);
    }
  }, [fitToData, mappingContext.defaultViewState]);

  const handleMapClick = useCallback((event: ReactMapGL.MapMouseEvent): void => {
    const clicked = event.features?.find((feature) => feature.layer?.id === PROVIDERS_LAYER_ID);
    if (!clicked?.properties) {
      return;
    }

    const clickedUniqueId = clicked.properties[MapPropertyKeysEnum.ProviderUniqueId] as string;
    const provider = providerFeatures.features
      .find((feature) => feature.properties.providerUniqueId === clickedUniqueId)?.properties;

    if (provider) {
      onProviderSelect(provider);
    }
  }, [onProviderSelect, providerFeatures]);

  useEffect(() => {
    if (!selectedProvider) {
      return;
    }
    mapRef.current?.easeTo({
      center: [selectedProvider.longitude, selectedProvider.latitude],
      duration: 1000,
    });
  }, [selectedProvider]);

  const selectedProviderFeature = useMemo(
    () => featureCollection(
      selectedProvider ? [point([selectedProvider.longitude, selectedProvider.latitude], selectedProvider)] : [],
    ),
    [selectedProvider],
  );

  const isSelectedProviderExpression = [
    "==",
    ["get", MapPropertyKeysEnum.ProviderUniqueId],
    selectedProvider?.providerUniqueId ?? null,
  ];

  const onMapMove = useCallback((next: ReactMapGL.ViewState): void => {
    // Lock rotation/pitch to match the county map's behavior.
    setViewState({ ...next, bearing: mappingContext.defaultBearing, pitch: mappingContext.defaultPitch });
  }, [mappingContext.defaultBearing, mappingContext.defaultPitch]);

  // `moveend` also fires when a fitBounds/easeTo animation finishes, so the initial fit and provider recenters report too.
  const handleMapMoveEnd = useCallback((): void => {
    onMapBoundsChange(mapRef.current?.getMap()?.getBounds() ?? null);
  }, [onMapBoundsChange]);

  const boundaryData: FeatureCollection<Geometry, ZipCodeModel> =
    boundaries ?? { features: [], type: "FeatureCollection" };
  const focusedZipBoundaryData: FeatureCollection<Geometry, ZipCodeModel> =
    focusedZipBoundary ?? { features: [], type: "FeatureCollection" };
  const countyBoundaryData: GeoJSON.FeatureCollection<Geometry, CountyModel> =
    countyBoundaries ?? { features: [], type: "FeatureCollection" };

  // Legend mirrors the active member layers, driven by the same toggles the layer control
  // sets (201360) — not by whether member points are rendered. Parity with county, whose
  // legend reflects showMembersWith/WithoutAccess. Provider entries follow when 202310 adds
  // provider layers/toggles the same way.
  const legendMembers: LegendItem[] = [
    ...(showMembersWithAccess ? [{ color: MEMBERS_WITH_ACCESS_COLOR, icon: QesIcons.Circle, label: "With Access" }] : []),
    ...(showMembersWithoutAccess ? [{ color: MEMBERS_WITHOUT_ACCESS_COLOR, icon: QesIcons.Circle, label: "Without Access" }] : []),
  ];

  // Terminated renders black only for users who can see terminations; everyone else sees the network colour.
  // Matches county, and both the legend row and the Filters flyout's Terminated option use the same gate.
  const canViewTerminations = canViewTerminatedProviders(userContext?.roles);

  // Action colours apply only on the latest published timeslice. The server already withholds terminated
  // providers on older ones but does not gate targeted/excluded, so this is load-bearing for those two.
  const showProviderActionStatuses = isLatestPublishedRunConfiguration;

  // Network/Prospect/Market follow the Provider Source selection
  const legendProviders: LegendItem[] = [
    ...(selectedProviderSources.includes(ProviderNetworkTypeEnum.Network)
      ? [{ color: NETWORK_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Network Provider" }] : []),
    ...(showProviderActionStatuses && canViewTerminations && hasTerminatedProviders
      ? [{ color: TERMINATED_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Terminated Provider" }] : []),
    ...(showProviderActionStatuses && hasTargetedProviders
      ? [{ color: TARGET_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Targeted Provider" }] : []),
    ...(showProviderActionStatuses && hasExcludedProviders
      ? [{ color: EXCLUDED_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Excluded Provider" }] : []),
    ...(selectedProviderSources.includes(ProviderNetworkTypeEnum.Prospect)
      ? [{ color: PROSPECT_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Prospect Provider" }] : []),
    ...(selectedProviderSources.includes(ProviderNetworkTypeEnum.Market)
      ? [{ color: MARKET_PROVIDER_COLOR, icon: QesIcons.ProviderMapPin, label: "Market Provider" }] : []),
  ];

  return (
    <MapboxSupportAlert>
      <div className="zip-coverage-map__layout">
        {leftPanel}
        <div className="zip-coverage-map__map-container">
          {mapOverlay}
          <ReactMapGL.Map
            {...viewState}
            {...mappingContext.defaultMapProps}
            interactiveLayerIds={[PROVIDERS_LAYER_ID]}
            mapStyle={mapStyle}
            onClick={handleMapClick}
            onLoad={onMapLoad}
            onMove={(e) => onMapMove(e.viewState)}
            onMoveEnd={handleMapMoveEnd}
            preserveDrawingBuffer={true}
            ref={mapRef}
            // Functional GL sizing (fills the sized container) — not visual styling.
            style={{ height: "100%", width: "100%" }}
          >
            <Source id={ZIP_LABEL_SOURCE_ID} type="geojson" data={boundaryData}>
              <Layer
                id={ZIP_BOUNDARY_LABEL_LAYER_ID}
                type="symbol"
                minzoom={ZIP_BOUNDARY_MIN_ZOOM_LEVEL}
                filter={showZipPoints || alwaysLabelZipPoints ? INCLUDE_ZIP_POINTS_FILTER : EXCLUDE_ZIP_POINTS_FILTER}
                layout={{
                  "text-allow-overlap": true,
                  "text-anchor": "center",
                  "text-field": ["get", MapPropertyKeysEnum.ZipCode],
                  "text-size": 14,
                  visibility: showZipBoundary ? "visible" : "none",
                }}
                paint={{
                  "text-color": "#2D2D2D",
                  "text-halo-color": "#fff",
                  "text-halo-width": 1,
                }}
              />
            </Source>

            <Source id={ZIP_FOCUSED_BOUNDARY_SOURCE_ID} type="geojson" data={focusedZipBoundaryData}>
              <Layer
                id={ZIP_FOCUSED_BOUNDARY_LAYER_ID}
                type="line"
                layout={{ visibility: showZipBoundary ? "visible" : "none" }}
                paint={{
                  "line-color": ZIP_BOUNDARY_COLOR,
                  "line-width": 1.5,
                }}
              />
            </Source>

            <Source id={COUNTY_BOUNDARY_SOURCE_ID} type="geojson" data={countyBoundaryData}>
              <Layer
                id={COUNTY_BOUNDARY_LAYER_ID}
                type="line"
                layout={{ visibility: showCounty ? "visible" : "none" }}
                paint={{
                  "line-color": COUNTY_BOUNDARY_COLOR,
                  "line-dasharray": [2, 2],
                  "line-width": 1.5,
                }}
              />
              <Layer
                id={COUNTY_BOUNDARY_LABEL_LAYER_ID}
                type="symbol"
                minzoom={COUNTY_MIN_ZOOM_LEVEL}
                layout={{
                  "text-allow-overlap": true,
                  "text-anchor": "center",
                  "text-field": ["get", MapPropertyKeysEnum.County],
                  "text-font": ["Avenir Next LT Pro Bold"],
                  "text-size": 16,
                  "visibility": showCounty ? "visible" : "none"
                }}
                paint={{
                  "text-color": "#2D2D2D",
                  "text-halo-color": "#fff",
                  "text-halo-width": 1
                }}
              />
            </Source>

            <Source id={MEMBER_SOURCE_ID} type="geojson" data={memberFeatures}>
              <Layer
                id={MEMBER_LAYER_ID}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  "icon-image": MEMBERS_IMAGE_ID,
                  "icon-size": 0.2,
                }}
                paint={{
                  "icon-color": [
                    "case",
                    ["==", ["get", MapPropertyKeysEnum.Covered], true],
                    MEMBERS_WITH_ACCESS_COLOR,
                    MEMBERS_WITHOUT_ACCESS_COLOR,
                  ],
                }}
                filter={[
                  "case",
                  ["==", ["get", MapPropertyKeysEnum.Covered], true], showMembersWithAccess,
                  ["==", ["get", MapPropertyKeysEnum.Covered], false], showMembersWithoutAccess,
                  false,
                ]}
              />
            </Source>

            <Source id={SELECTED_PROVIDER_SOURCE_ID} type="geojson" data={selectedProviderFeature}>
              <Layer
                id={PROVIDER_HIGHLIGHTED_LAYER_ID}
                type="circle"
                paint={{
                  "circle-blur": 1,
                  "circle-color": PROVIDER_ICON_SHADOW_COLOR,
                  "circle-radius": 40,
                }}
              />
            </Source>

            {/* Providers Layer (202310). Declared after every boundary source so pins draw above the lines.
              Expression docs: https://docs.mapbox.com/style-spec/reference/expressions/ */}
            <Source id={PROVIDERS_SOURCE_ID} type="geojson" data={providerFeatures}>
              <Layer
                id={PROVIDERS_LAYER_ID}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  "icon-image": [
                    "image",
                    PROVIDER_ICON_IMAGE_ID,
                    {
                      params: {
                        icon_center_border: "black",
                        icon_center_color: "white",
                        icon_primary_border: "black",
                        icon_primary_color: [
                          "case",
                          // Action statuses first, so they take precedence over network type.
                          ...(showProviderActionStatuses
                            ? [
                              ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTargeted],
                              TARGET_PROVIDER_COLOR,
                              ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyExcluded],
                              EXCLUDED_PROVIDER_COLOR,
                              ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTerminated],
                              canViewTerminations ? TERMINATED_PROVIDER_COLOR : NETWORK_PROVIDER_COLOR,
                            ]
                            : []),
                          ["==", ["get", MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Market],
                          MARKET_PROVIDER_COLOR,
                          ["==", ["get", MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Prospect],
                          PROSPECT_PROVIDER_COLOR,
                          // Default
                          NETWORK_PROVIDER_COLOR,
                        ],
                      },
                    },
                  ],
                  "icon-size": [
                    "interpolate",
                    // Linearly scale the size between zoom level 10 and 20; the selected pin holds a
                    // fixed, larger size at both stops so it stays distinguishable at any zoom.
                    ["linear"],
                    ["zoom"],
                    10,
                    ["case", isSelectedProviderExpression, PROVIDER_ICON_SELECTED_SIZE, PROVIDER_ICON_UNSELECTED_MIN_SIZE],
                    20,
                    ["case", isSelectedProviderExpression, PROVIDER_ICON_SELECTED_SIZE, PROVIDER_ICON_UNSELECTED_MAX_SIZE],
                  ],
                  "symbol-sort-key": [
                    "case",
                    isSelectedProviderExpression,
                    100,
                    ...(showProviderActionStatuses
                      ? [
                        ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTargeted],
                        60,
                        ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyExcluded],
                        50,
                        ["==", ["get", MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTerminated],
                        40,
                      ]
                      : []),
                    ["==", ["get", MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Prospect],
                    30,
                    ["==", ["get", MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Market],
                    20,
                    // Default sort key for network providers.
                    10,
                  ],
                }}
              />
            </Source>

            {providerPopup}

            <NavigationControl data-html2canvas-ignore="true" position="top-right" showCompass={false} />
            <RecenterControl
              ignoreInMapExport={true}
              imagePath={`${imagesDir}Recenter.png`}
              mapRef={mapRef}
              onClick={recenter}
              position="top-right"
            />
            {showLayerControl && (
              <ZipCoverageMapLayerControl
                ignoreInMapExport={true}
                imagePath={`${imagesDir}Layer_Hover.png`}
                mapRef={mapRef}
                position="top-right"
                lightStyle={mappingContext.qesLightStyle}
                outdoorsStyle={mappingContext.qesOutdoorsStyle}
                mapStyle={mapStyle}
                onMapStyleChange={onMapStyleChange}
                showMembersWithAccess={showMembersWithAccess}
                onShowMembersWithAccessChange={onShowMembersWithAccessChange}
                showMembersWithoutAccess={showMembersWithoutAccess}
                onShowMembersWithoutAccessChange={onShowMembersWithoutAccessChange}
                showZipBoundary={showZipBoundary}
                onShowZipBoundaryChange={onShowZipBoundaryChange}
                showZipPoints={showZipPoints}
                onShowZipPointsChange={onShowZipPointsChange}
                showCounty={showCounty}
                onShowCountyChange={onShowCountyChange}
              />
            )}
            {boundaries && boundaries.features.length > 0 && (
              <ZipCoverageMapLegendControl members={legendMembers} providers={legendProviders} position="bottom-right" mapRef={mapRef} />
            )}
          </ReactMapGL.Map>
        </div>
      </div>
    </MapboxSupportAlert>
  );
};

export default ZipCoverageMap;
